import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const fmt = n => Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => { if (!d) return '-'; const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/^(\d+) (\w+) (\d+)$/, '$1 $2, $3'); };

export default function ConsumerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ reading_date: '', meter_reading: '' });
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addData, setAddData] = useState({ reading_date: '', meter_reading: '' });
  const [adding, setAdding] = useState(false);

  const load = () => { api.get(`/consumers/${id}`).then(r => setData(r.data)); };
  useEffect(load, [id]); // eslint-disable-line

  if (!data) return <div className="loading"><div className="spinner"></div></div>;
  const { consumer: c, readings, bills, payments } = data;

  const statusBadge = s => {
    const map = { Paid: 'badge-green', Unpaid: 'badge-orange', Overdue: 'badge-red', Partial: 'badge-blue' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditData({ reading_date: r.reading_date, meter_reading: String(r.meter_reading) });
    setShowAdd(false);
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async () => {
    if (!editData.reading_date || !editData.meter_reading) return;
    setSaving(true);
    try {
      await api.put(`/readings/${editId}`, {
        reading_date: editData.reading_date,
        meter_reading: parseInt(editData.meter_reading, 10),
      });
      setEditId(null);
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to update reading');
    }
    setSaving(false);
  };

  const deleteReading = async (r) => {
    if (!window.confirm(`Delete reading on ${fmtDate(r.reading_date)}?\n\nMeter reading: ${r.meter_reading}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/readings/${r.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete reading');
    }
  };

  const openAdd = () => {
    setShowAdd(true);
    setEditId(null);
    setAddData({ reading_date: new Date().toISOString().split('T')[0], meter_reading: '' });
  };

  const cancelAdd = () => { setShowAdd(false); };

  const saveAdd = async () => {
    if (!addData.reading_date || !addData.meter_reading) return;
    setAdding(true);
    try {
      await api.post('/readings', {
        consumer_id: parseInt(id, 10),
        reading_date: addData.reading_date,
        meter_reading: parseInt(addData.meter_reading, 10),
      });
      setShowAdd(false);
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to add reading');
    }
    setAdding(false);
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <h1 className="page-title" style={{margin:0}}>{c.name}</h1>
        <Link to={`/consumers/${id}/edit`} className="btn btn-primary">Edit</Link>
      </div>

      <div className="card">
        <h3>Consumer Profile</h3>
        <div style={{display:'grid', gridTemplateColumns:'140px 1fr', gap:'8px 16px', marginTop:12}}>
          <span style={{color:'#666'}}>Account Code</span><span><strong>{c.account_code}</strong></span>
          <span style={{color:'#666'}}>Barangay</span><span>{c.barangay_name}</span>
          <span style={{color:'#666'}}>Address</span><span>{c.address || '-'}</span>
          <span style={{color:'#666'}}>Mobile</span><span>{c.mobile || '-'}</span>
          <span style={{color:'#666'}}>Type</span><span>{c.consumer_type}</span>
          <span style={{color:'#666'}}>Status</span><span>{c.status}</span>
          <span style={{color:'#666'}}>Registered</span><span>{c.date_registered || '-'}</span>
        </div>
      </div>

      <div className="card">
        <div className="flex-between" style={{marginBottom: readings.length > 0 || showAdd ? 12 : 0}}>
          <h3 style={{margin:0}}>Meter Readings</h3>
          {!showAdd && <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff'}} onClick={openAdd}>+ Add Reading</button>}
        </div>

        {showAdd && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: '#e8f5e9', borderRadius: 6, marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>New Reading:</span>
            <input type="date" className="input" style={{ width: 160, padding: '6px 8px', fontSize: 13 }} value={addData.reading_date}
              onChange={e => setAddData(d => ({...d, reading_date: e.target.value}))} />
            <input type="number" className="input" placeholder="Meter reading" style={{ width: 130, padding: '6px 8px', fontSize: 13 }} value={addData.meter_reading}
              onChange={e => setAddData(d => ({...d, meter_reading: e.target.value}))} min="0" />
            <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff',padding:'6px 16px',fontSize:13}} onClick={saveAdd} disabled={adding}>{adding ? 'Saving...' : 'Save'}</button>
            <button className="btn btn-sm" style={{background:'#eee',padding:'6px 16px',fontSize:13}} onClick={cancelAdd}>Cancel</button>
          </div>
        )}

        {readings.length === 0 && !showAdd ? <p style={{color:'#999'}}>No readings</p> : readings.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th className="text-right">Reading</th><th style={{width:120, textAlign:'center'}}>Actions</th></tr></thead>
              <tbody>{readings.map(r => (
                editId === r.id ? (
                  <tr key={r.id} style={{background:'#fffbe6'}}>
                    <td><input type="date" className="input" style={{width:150, padding:'4px 6px', fontSize:13}} value={editData.reading_date} onChange={e => setEditData(d => ({...d, reading_date: e.target.value}))} /></td>
                    <td className="text-right"><input type="number" className="input" style={{width:100, padding:'4px 6px', fontSize:13, textAlign:'right'}} value={editData.meter_reading} onChange={e => setEditData(d => ({...d, meter_reading: e.target.value}))} min="0" /></td>
                    <td style={{textAlign:'center'}}>
                      <button className="btn btn-sm" style={{background:'#2e7d32',color:'#fff',marginRight:4,padding:'3px 10px',fontSize:12}} onClick={saveEdit} disabled={saving}>{saving ? '...' : 'Save'}</button>
                      <button className="btn btn-sm" style={{background:'#eee',padding:'3px 10px',fontSize:12}} onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id}>
                    <td>{fmtDate(r.reading_date)}</td>
                    <td className="text-right">{r.meter_reading}</td>
                    <td style={{textAlign:'center'}}>
                      <button className="btn btn-sm" style={{background:'#1976d2',color:'#fff',marginRight:4,padding:'3px 10px',fontSize:12}} onClick={() => startEdit(r)}>Edit</button>
                      <button className="btn btn-sm" style={{background:'#d32f2f',color:'#fff',padding:'3px 10px',fontSize:12}} onClick={() => deleteReading(r)}>Delete</button>
                    </td>
                  </tr>
                )
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Bills</h3>
        {bills.length === 0 ? <p style={{color:'#999'}}>No bills</p> : (
          <div className="table-wrap">
            <table><thead><tr><th>Period</th><th className="text-right">Consumption</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
              <tbody>{bills.map(b => <tr key={b.id}><td>{b.billing_period}</td><td className="text-right">{b.consumption} cu.m.</td><td className="text-right">PHP {fmt(b.total_amount)}</td><td>{statusBadge(b.status)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Payments</h3>
        {payments.length === 0 ? <p style={{color:'#999'}}>No payments</p> : (
          <div className="table-wrap">
            <table><thead><tr><th>Date</th><th>Period</th><th className="text-right">Amount</th><th>Receipt</th></tr></thead>
              <tbody>{payments.map(p => <tr key={p.id}><td>{p.payment_date}</td><td>{p.billing_period}</td><td className="text-right">PHP {fmt(p.amount)}</td><td>{p.receipt_no || '-'}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
